/*
 * FOR SALE — achat direct depuis la fiche (ACHAT-05 côté thème ; choix C11 et C13 du contrat
 * docs/architecture/prix-et-achat-direct.md). Module chargé seulement si le réglage fs_direct_purchase est actif et le
 * service valide (racine [data-fs-purchase-enabled] : accueil ou page d'une annonce), APRÈS fs-account.js : session,
 * connexion Discord et appels au service passent par window.FSAccount.
 *
 * Fiche ([data-fs-ld] dans [data-fs-detail]) : bouton « Acheter ce compte » [data-fs-purchase] rendu par le Liquid dans la
 * zone commerce, invisible (place gardée, fs-purchase.css) tant qu'il n'est pas prêt. Accueil ([data-fs-purchase-verify]) :
 * le Liquid ne peut pas lire plus de 20 produits par page (all_products) ; le script vérifie /products/fs-<handle>.js
 * (publié, disponible, une variante au prix affiché) avant de le montrer, sinon « Indisponible pour le moment ». Page de
 * l'annonce : produit déjà vérifié par le Liquid, variante fournie.
 *
 * Clic : sans session, modale « Pour recevoir votre compte, connectez-vous avec Discord » → connexion (intention
 * « purchase » gardée par fs-account.js) → retour sur la même fiche, modale rouverte. Avec session : modale accessible
 * (récapitulatif, « J'accepte les conditions générales de vente », « Payer 120 € » inactif tant que la case n'est pas
 * cochée) ; puis POST /purchases/intent (jeton
 * opaque, prix revérifié) en même temps que /cart/clear.js, ensuite /cart/add.js (une ligne, quantité 1, propriété cachée
 * _fs_intent) et /checkout.
 * Aucune réservation au clic : le premier paiement gagne (stock 1, DENY). Aucun montant n'est calculé ici : le prix vient
 * du Liquid (price_minor) et le service le revérifie. Tout texte venu du service ou du Liquid passe par textContent ;
 * toute adresse est vérifiée. Les fonctions pures sont exportées pour les tests Node (purchase-ui.test.mjs).
 * Textes et montants dans la langue de la page (fs-i18n.js, zone i18n/purchase.json) ; les erreurs du service sont
 * lues par leur statut et leur code (intentOutcome), jamais par leur texte.
 */
import { dialog as uiDialog } from './fs-ui-dialog.js';
import { el as h, badge, link } from './fs-ui.js';
import { t, number } from './fs-i18n.js';

export const HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
export const NBSP = '\u00a0';
const TOKEN = /^[A-Za-z0-9._~+/=-]{8,2048}$/;
const TERMS_VERSION = /^[A-Za-z0-9._-]{1,64}$/;
const GAME_NAMES = Object.freeze({ waruniverse: 'WarUniverse', redgalaxy: 'Red Galaxy', darkorbit: 'DarkOrbit', novahorizon: 'Nova Horizon' });
export const TERMS_FALLBACK = '/policies/terms-of-service';
/** Textes du parcours (backlog/annexes/textes-proposes.md § 4.2 et 4.3), dans la langue de la page (i18n/purchase.json). */
export const TEXT = Object.freeze({
  login: t('purchase.login'),
  expired: t('purchase.expired'),
  denied: t('purchase.denied'),
  storage: t('purchase.storage'),
  delivery: t('purchase.delivery'),
  hint: t('purchase.hint'),
  terms: t('purchase.terms_before'),
  termsLink: t('purchase.terms_link'),
  termsAfter: t('purchase.terms_after'),
  member: t('purchase.member'),
  restricted: t('purchase.restricted'),
  price: t('purchase.price'),
  termsChanged: t('purchase.terms_changed'),
  consents: t('purchase.consents'),
  discord: t('purchase.discord'),
  limit: t('purchase.limit'),
  disabled: t('purchase.disabled'),
  error: t('purchase.error'),
  cart: t('purchase.cart'),
  noTerms: t('purchase.no_terms'),
  busy: t('purchase.busy'),
  off: t('purchase.off'),
  unavailable: t('purchase.unavailable'),
});
const sold = (gameName) => t('purchase.sold', { game: gameName || 'Space Syndicate' });
const reserved = (gameName) => t('purchase.reserved', { game: gameName || 'Space Syndicate' });

const plain = (value, max) => (typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, '').trim().slice(0, max) : '');

/**
 * Centimes → prix en euros dans la langue de la page (INTL-10, Intl.NumberFormat, jamais de conversion) : « 1 290 € »
 * (fr, ru), « €1,290 » (en), « 1290 € » (es), « €1.290 » (tr) ; centimes seulement s'il y en a ; espaces insécables
 * classiques (même rendu que le snippet fs-listing-price) ; '' si la valeur est invalide.
 */
export function formatEuro(minor) {
  if (!Number.isSafeInteger(minor) || minor <= 0) return '';
  const digits = minor % 100 ? 2 : 0;
  return number(minor / 100, { style: 'currency', currency: 'EUR', minimumFractionDigits: digits, maximumFractionDigits: digits })
    .replace(/[\u202f ]/g, NBSP);
}
export const payLabel = (priceText) => t('purchase.pay', { price: priceText });

/** Identifiant numérique d'une variante (nombre, chaîne d'entier ou GID Shopify de variante), sinon null. */
export function variantNumericId(raw) {
  const text = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.replace(/^gid:\/\/shopify\/ProductVariant\//, '') : '';
  if (!/^[1-9]\d{0,15}$/.test(text)) return null;
  const id = Number(text);
  return Number.isSafeInteger(id) ? id : null;
}

/**
 * Attributs du bouton (dataset) → { listing, product, variant, verify, priceMinor, priceText, game, gameName, reference,
 * title, server }, ou null. Le produit est toujours fs-<annonce> ; le texte du prix suit le montant (jamais l'inverse).
 */
export function readPurchaseButton(data) {
  if (!data || typeof data !== 'object') return null;
  const listing = String(data.fsPurchase ?? '');
  const priceMinor = /^[1-9]\d{0,12}$/.test(String(data.fsPurchasePrice ?? '')) ? Number(data.fsPurchasePrice) : 0;
  if (!HANDLE.test(listing) || !priceMinor) return null;
  const product = data.fsPurchaseProduct === undefined ? `fs-${listing}` : String(data.fsPurchaseProduct);
  if (product !== `fs-${listing}`) return null;
  const formatted = formatEuro(priceMinor);
  const given = plain(data.fsPurchasePriceText, 40).replace(/ /g, NBSP);
  const game = HANDLE.test(String(data.fsPurchaseGame ?? '')) ? data.fsPurchaseGame : '';
  return {
    listing, product, variant: variantNumericId(data.fsPurchaseVariant), verify: data.fsPurchaseVerify !== undefined,
    priceMinor, priceText: given === formatted ? given : formatted, game,
    gameName: plain(data.fsPurchaseGameName, 60) || GAME_NAMES[game] || game,
    reference: plain(data.fsPurchaseRef, 40), title: plain(data.fsPurchaseTitle, 160), server: plain(data.fsPurchaseServer, 60),
  };
}

/** Produit Shopify (/products/<handle>.js, prix en centimes) : publié, disponible, une seule variante au prix affiché. */
export function productVerdict(product, priceMinor) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return { ok: false, reason: 'missing' };
  if (variants.length > 1) return { ok: false, reason: 'variants' };
  const [variant] = variants;
  if (product.available !== true || variant?.available !== true) return { ok: false, reason: 'unavailable' };
  if (!Number.isSafeInteger(variant.price) || variant.price !== priceMinor) return { ok: false, reason: 'price' };
  const variantId = variantNumericId(variant.id);
  return variantId ? { ok: true, variantId } : { ok: false, reason: 'missing' };
}

/**
 * État réel de l'achat d'une carte (décision 024, 14 octobre 2026) : réponse de /products/fs-<handle>.js → verdict, dans
 * le même ordre que la fiche (fs-listing-card.liquid) : offre (une seule variante) au prix affiché d'abord, puis stock.
 * 404 : offre absente ou dépubliée (« missing ») ; plusieurs variantes (« variants ») ou autre prix (« price ») ; au prix
 * mais plus disponible (« unavailable ») ; tout autre échec (réseau, 429, 5xx, réponse illisible) : inconnu (« error »),
 * la carte garde son état (jamais rétrogradée ni restaurée sur une panne passagère).
 */
export function offerResponseVerdict(status, json, priceMinor) {
  if (status === 404) return { ok: false, reason: 'missing' };
  if (status !== 200 || !json) return { ok: false, reason: 'error' };
  const variants = Array.isArray(json.variants) ? json.variants : [];
  if (!variants.length) return { ok: false, reason: 'missing' };
  if (variants.length > 1) return { ok: false, reason: 'variants' };
  if (!Number.isSafeInteger(variants[0]?.price) || variants[0].price !== priceMinor) return { ok: false, reason: 'price' };
  return productVerdict(json, priceMinor);
}

/** « 540.0 » → 54000 (sans flottant), sinon null. */
export function amountMinor(amount) {
  const match = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(typeof amount === 'string' ? amount : '');
  return match ? Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0')) : null;
}

export const STOREFRONT_API = '/api/2026-10/graphql.json';
export const OFFER_BATCH = 25; // coût ≈ 5 par produit, plafond 1 000

export function offerBatchQuery(products, country) {
  const context = /^[A-Z]{2}$/.test(country || '') ? ` @inContext(country: ${country})` : '';
  const vars = products.map((_, i) => `$h${i}: String!`).join(', ');
  const body = products.map((_, i) => `p${i}: product(handle: $h${i}) { availableForSale variants(first: 2) { nodes { id availableForSale price { amount currencyCode } } } }`).join(' ');
  return { query: `query Offers(${vars})${context} { ${body} }`, variables: Object.fromEntries(products.map((product, i) => [`h${i}`, product])) };
}

/** Verdict d'offerResponseVerdict depuis la Storefront API ; autre devise : inconnu. */
export function storefrontVerdict(node, priceMinor, currency) {
  if (node === null) return { ok: false, reason: 'missing' };
  const variants = Array.isArray(node?.variants?.nodes) ? node.variants.nodes : null;
  if (!variants) return { ok: false, reason: 'error' };
  if (!variants.length) return { ok: false, reason: 'missing' };
  if (variants.length > 1) return { ok: false, reason: 'variants' };
  const [variant] = variants;
  if (currency && variant?.price?.currencyCode !== currency) return { ok: false, reason: 'error' };
  const amount = amountMinor(variant?.price?.amount);
  if (amount === null) return { ok: false, reason: 'error' };
  if (amount !== priceMinor) return { ok: false, reason: 'price' };
  if (node.availableForSale !== true || variant.availableForSale !== true) return { ok: false, reason: 'unavailable' };
  const variantId = variantNumericId(variant.id);
  return variantId ? { ok: true, variantId } : { ok: false, reason: 'missing' };
}

/**
 * État affiché selon le verdict de l'offre : 'priced' (achetable), null (inconnu : inchangé), « reserved » (au prix mais
 * plus disponible), « soon » (absente, dépubliée, à un autre prix ou à plusieurs variantes : aucun prix achetable, P-12).
 */
export function offerState(verdict) {
  if (!verdict || verdict.reason === 'error') return null;
  if (verdict.ok) return 'priced';
  return verdict.reason === 'unavailable' ? 'reserved' : 'soon';
}

/* Cartes rétrogradées : rendu d'origine gardé pour la restauration (offre republiée). */
const downgraded = new WeakMap();

/**
 * Carte du catalogue vers son état réel. 'soon' / 'reserved' : prix remplacé par « Bientôt disponible à l'achat » ou par le
 * badge « Réservée » (mêmes classes que le Liquid, fs-listing-card), data-fs-price-state mis à jour, nom accessible de
 * « Voir l'annonce » sans le prix. 'priced' : rendu d'origine restauré (offre de retour). Rend true si la carte a changé.
 */
export function applyCardState(card, state) {
  const block = card?.querySelector?.('.fs-lc-price');
  if (!block || !card.dataset) return false;
  const open = card.querySelector('.fs-lc-open');
  if (state === 'priced') {
    const saved = downgraded.get(card);
    if (!saved || card.dataset.fsPriceState === 'priced') return false;
    block.replaceChildren(...saved.children);
    card.dataset.fsPriceState = 'priced';
    card.classList.remove?.('fs-lc--soon');
    if (open && saved.name) open.setAttribute('aria-label', saved.name);
    downgraded.delete(card);
    return true;
  }
  const label = state === 'reserved' ? card.dataset.fsLabelReserved : state === 'soon' ? card.dataset.fsLabelSoon : '';
  if (!label || card.dataset.fsPriceState === state) return false;
  const current = card.dataset.fsPriceState;
  if (current !== 'priced' && !downgraded.has(card)) return false;
  if (!downgraded.has(card)) {
    downgraded.set(card, { children: [...(block.children ?? [])], name: open?.getAttribute('aria-label') || '',
      priceText: block.querySelector?.('[data-fs-price]')?.textContent?.trim() || '' });
  }
  const saved = downgraded.get(card);
  block.replaceChildren(state === 'reserved'
    ? badge(label, { attrs: { class: 'fs-badge--state fs-lc-state', 'data-fs-state': 'reserved' } })
    : h('span', { class: 'fs-price fs-price--card fs-price--soon fs-lc-price-text', text: label }));
  card.dataset.fsPriceState = state;
  if (state === 'soon') card.classList.add('fs-lc--soon'); else card.classList.remove?.('fs-lc--soon');
  if (open && saved.priceText && saved.name.endsWith(`, ${saved.priceText}`)) open.setAttribute('aria-label', `${saved.name.slice(0, -saved.priceText.length)}${label}`);
  return true;
}

/** Corps de POST /purchases/intent : annonce et consentements, rien d'autre (ni montant, ni pseudo). */
export function intentBody({ listing, termsVersion } = {}) {
  if (!HANDLE.test(listing || '') || !TERMS_VERSION.test(termsVersion || '')) return null;
  return { listing, consents: { terms: true, termsVersion } };
}

/** État du bouton « Payer » : prêt quand la case des CGV est cochée ; sinon le message lié par aria-describedby. */
export function payState({ terms }) {
  const ready = terms === true;
  return { ready, hint: ready ? '' : TEXT.hint };
}

/**
 * Réponse de POST /purchases/intent (201 Created, équipe Achat) → { kind, message } ou { kind: 'ok', token, variantId }.
 * kind : ok | auth | member | restricted | sold | reserved | unavailable | price | terms | limit | disabled | error. Le prix
 * et la devise rendus doivent être ceux affichés. Codes du service : 403 not_member / member_restricted ; 409 listing_sold
 * (seul « vendu »), listing_reserved (achat en cours d'un autre membre), price_mismatch, terms_outdated, et tout autre 409
 * (listing_not_purchasable, listing_control_busy, listing_in_trash…) : momentanément indisponible, jamais « vendu » ;
 * 422 invalid_consents / invalid_body ; 429 rate_limited / busy ; 503 listing_control_* (indisponible), purchase_disabled,
 * purchase_unavailable, discord_unavailable.
 */
export function intentOutcome(status, body, { priceMinor, gameName } = {}) {
  const data = body && typeof body === 'object' ? body : {};
  const code = typeof data.error === 'string' ? data.error : '';
  if (status === 200 || status === 201) {
    const variantId = variantNumericId(data.variantId);
    if (typeof data.token !== 'string' || !TOKEN.test(data.token) || !variantId) return { kind: 'error', message: TEXT.error };
    if (data.priceMinor !== priceMinor || (data.currency !== undefined && data.currency !== 'EUR')) return { kind: 'price', message: TEXT.price };
    return { kind: 'ok', token: data.token, variantId };
  }
  if (status === 401) return { kind: 'auth', message: TEXT.expired };
  if (status === 403) return /restricted|pending|timeout/.test(code) ? { kind: 'restricted', message: TEXT.restricted } : { kind: 'member', message: TEXT.member };
  if (status === 409 && code === 'price_mismatch') return { kind: 'price', message: TEXT.price };
  if (status === 409 && code === 'terms_outdated') return { kind: 'terms', message: TEXT.termsChanged };
  if (status === 409 && code === 'listing_sold') return { kind: 'sold', message: sold(gameName) };
  if (status === 409 && code === 'listing_reserved') return { kind: 'reserved', message: reserved(gameName) };
  if (status === 409) return { kind: 'unavailable', message: TEXT.unavailable };
  if (status === 422) return { kind: 'error', message: TEXT.consents };
  if (status === 429) return { kind: 'limit', message: TEXT.limit };
  if (status === 503 && code === 'discord_unavailable') return { kind: 'error', message: TEXT.discord };
  if (status === 503 && /^listing_control_[a-z_]+$/.test(code)) return { kind: 'unavailable', message: TEXT.unavailable };
  if (status === 503) return { kind: 'disabled', message: TEXT.disabled };
  return { kind: 'error', message: TEXT.error };
}

/**
 * Issue du panier après une intention acceptée (fillCart : 'unavailable' si /cart/add.js répond 422, sinon 'error').
 * Un produit épuisé n'est pas une vente confirmée (une commande impayée peut tenir le stock) : jamais « acheté » ici,
 * « momentanément indisponible » et le bouton reste actif ; un nouvel essai obtient du service listing_sold ou
 * listing_reserved s'il y a lieu.
 */
export const cartOutcome = (cart) => (cart === 'unavailable' ? { kind: 'unavailable', message: TEXT.unavailable } : { kind: 'error', message: TEXT.cart });

/** Corps de /cart/add.js : une ligne, quantité 1, intention en propriété cachée (préfixe « _ », masquée au checkout). */
export const cartAddBody = (variantId, token) => ({ items: [{ id: variantId, quantity: 1, properties: { _fs_intent: token } }] });

/** Racine de la boutique (window.Shopify.routes.root, langue comprise), toujours un chemin local terminé par « / ». */
export function shopRoot(win) {
  const root = win?.Shopify?.routes?.root;
  if (typeof root !== 'string' || !/^\/(?:[A-Za-z0-9_-]+\/?)*$/.test(root)) return '/';
  return root.endsWith('/') ? root : `${root}/`;
}

/** Conditions générales de vente : chemin local ou https, sinon la page des règles Shopify. */
export function termsHref(raw) {
  const text = String(raw || '').trim();
  if (/^\/(?!\/)[^\s]*$/.test(text)) return text;
  try { if (new URL(text).protocol === 'https:') return text; } catch { /* illisible */ }
  return TERMS_FALLBACK;
}

/** Invitation au serveur (discord.gg/<code> ou discord.com/invite/<code>), sinon ''. */
export function inviteHref(raw) {
  const text = String(raw || '').trim();
  return /^https:\/\/(?:discord\.gg|discord\.com\/invite)\/[A-Za-z0-9-]{2,32}$/.test(text) ? text : '';
}

/* ------------------------------------------------------------------ carte « Trophée » */

/**
 * Instantané de la carte de l'annonce, pris au départ vers le checkout et relu par la page « Trophée » d'après paiement
 * (fs-order-confirmed.js) : l’annonce vendue repasse en brouillon et n’est plus lisible par le thème. Clé fs-trophy du stockage
 * local du navigateur, objet { [annonce]: instantané }, 6 annonces au plus, la plus récente en premier. Rien de privé : ce que la
 * fiche publique affichait (vaisseau, flotte, chiffres), jamais de prix ni d'identifiant de session.
 */
export const TROPHY_KEY = 'fs-trophy';
const TROPHY_MAX = 6;
const TROPHY_TIERS = ['rarest', 'paid'];
const TROPHY_FIGS = ['level', 'rank', 'weapon'];
const shopImage = (value) => (typeof value === 'string' && /^(?:https:)?\/\/[^\s"'<>]+$/.test(value) && value.length <= 600 ? value : '');
/** srcset dont chaque adresse est une image servie en https, sinon ''. */
const shopSrcset = (value) => { const text = plain(value, 1200); return text.split(',').every((part) => !part.trim() || shopImage(part.trim().split(/\s+/)[0])) ? text : ''; };

/** Lecture de la carte rendue par le Liquid (fs-listing-card / fs-listing-bay / fs-listing-fleet / fs-listing-figures). */
export function readCard(doc, listing) {
  const card = [...doc.querySelectorAll('[data-fs-listing]')].find((node) => node.dataset.fsHandle === listing) || doc.querySelector('[data-fs-ld] [data-fs-listing], [data-fs-listing]');
  if (!card) return null;
  const hero = card.querySelector('img.fs-bay-hero');
  const text = (node) => (node ? node.textContent.replace(/\s+/g, ' ').trim() : '');
  return {
    hero: hero ? { src: hero.currentSrc || hero.getAttribute('src') || '', srcset: hero.getAttribute('srcset') || '', width: hero.getAttribute('width') || '' } : null,
    name: text(card.querySelector('[data-fs-bay-name]')), tier: card.querySelector('.fs-bay-slot.is-hero')?.dataset.tier || '', tierLabel: text(card.querySelector('[data-fs-bay-tier]')),
    fleet: { main: text(card.querySelector('.fs-fleet-n')), subs: [...card.querySelectorAll('.fs-fleet-sub')].map(text) },
    figs: [...card.querySelectorAll('.fs-fig[data-fig]')].map((node) => ({ key: node.dataset.fig, label: text(node.querySelector('dt')), value: text(node.querySelector('dd')) })),
  };
}

/** Lecture de la carte + données du bouton d'achat → instantané nettoyé, ou null. */
export function cardSnapshot(read, data, now = Date.now()) {
  if (!data || !HANDLE.test(data.listing || '')) return null;
  const card = read && typeof read === 'object' ? read : {};
  const src = shopImage(card.hero?.src);
  const width = Number.parseInt(card.hero?.width, 10);
  return {
    v: 1, at: new Date(now).toISOString(), listing: data.listing, game: HANDLE.test(data.game || '') ? data.game : '', reference: plain(data.reference, 40), title: plain(data.title, 160),
    ship: src ? { name: plain(card.name, 80), src, srcset: shopSrcset(card.hero.srcset), width: Number.isSafeInteger(width) && width > 0 && width < 4000 ? width : 0 } : null,
    tier: TROPHY_TIERS.includes(card.tier) ? card.tier : '', tierLabel: plain(card.tierLabel, 40),
    fleet: { main: plain(card.fleet?.main, 120), subs: (Array.isArray(card.fleet?.subs) ? card.fleet.subs : []).map((item) => plain(item, 80)).filter(Boolean).slice(0, 3) },
    figs: (Array.isArray(card.figs) ? card.figs : []).filter((fig) => TROPHY_FIGS.includes(fig?.key)).slice(0, 3).map((fig) => ({ key: fig.key, label: plain(fig.label, 40), value: plain(fig.value, 40) })),
  };
}

/** Range l'instantané (le plus récent d'abord, 6 au plus) ; stockage indisponible ou contenu illisible : sans effet ni erreur. */
export function rememberCard(snapshot, storage) {
  if (!snapshot || !storage) return;
  try {
    let saved = {};
    try { const parsed = JSON.parse(storage.getItem(TROPHY_KEY) || '{}'); if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) saved = parsed; } catch { saved = {}; }
    const rest = Object.entries(saved).filter(([key]) => key !== snapshot.listing && HANDLE.test(key));
    storage.setItem(TROPHY_KEY, JSON.stringify(Object.fromEntries([[snapshot.listing, snapshot], ...rest].slice(0, TROPHY_MAX))));
  } catch { /* stockage refusé (navigation privée stricte) : la page « Trophée » montrera la carte réduite. */ }
}

/** Issue de la connexion (fs-account.js, événement « fs:session ») → { listing, ok, error } pour un achat, sinon null. */
export function returnIntent(detail) {
  const intent = detail?.intent;
  if (!intent || intent.kind !== 'purchase' || !HANDLE.test(intent.listing || '')) return null;
  return { listing: intent.listing, ok: detail.ok === true, error: typeof detail.error === 'string' ? detail.error.slice(0, 80) : '' };
}

/** Message d'une connexion revenue en échec. */
const loginError = (code) => (/restricted|timeout|pending/.test(code) ? TEXT.restricted : /member/.test(code) ? TEXT.member : /denied/.test(code) ? TEXT.denied : TEXT.expired);

/* ------------------------------------------------------------------ interface */

const external = () => h('span', { class: 'fs-visually-hidden', text: t('listing.new_tab') });
const discordIcon = () => h('span', { class: 'fs-discord-icon', 'aria-hidden': 'true' });
const serviceBase = (raw) => {
  try {
    const url = new URL(String(raw || '').trim());
    const local = url.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(url.hostname);
    if ((url.protocol !== 'https:' && !local) || url.username || url.password || url.search || url.hash) return '';
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch { return ''; }
};
const VERIFY_TTL_MS = 60_000;

/* Vers le checkout : pas de transition de page (le checkout ne s'y inscrit pas ; sans cela, la promesse de la transition
   abandonnée reste rejetée sans traitement, signalé par le labo). */
function toCheckout(url) {
  window.addEventListener('pageswap', (event) => {
    const transition = event.viewTransition;
    if (!transition) return;
    for (const promise of [transition.ready, transition.finished, transition.updateCallbackDone]) promise?.catch?.(() => {});
    transition.skipTransition();
  }, { once: true });
  window.location.assign(url);
}

/**
 * Cartes du catalogue ([data-fs-offer-check]) : offres des cartes visibles lues par lots de 25 (Storefront API, 0,3 s
 * au lieu de 2,5 à 3 s pour 49 cartes), repli sur /products/fs-<handle>.js ; une lecture par produit et par minute. Achat
 * impossible : état réel de la carte (applyCardState). Indépendant de la session.
 */
/* Vérifications actives, par racine : arrêtées au retrait de leur section (éditeur de thème) ou de leur racine. */
const cardChecks = new Map();

/** Arrête les vérifications des racines contenues dans `node` (ou la racine elle-même) : observateur, minuterie, écouteur. */
export function stopCardChecks(node) {
  for (const [root, stop] of [...cardChecks]) if (root === node || node?.contains?.(root)) stop();
}

export function checkCards(root) {
  if (root.dataset.fsOfferChecks) return null;
  root.dataset.fsOfferChecks = 'true';
  let stopped = false;
  const shop = shopRoot(window), cache = new Map(), queue = [], checkedAt = new WeakMap(), shown = new Set();
  let running = 0;
  const read = (product, priceMinor) => {
    const key = `${product}:${priceMinor}`, known = cache.get(key);
    if (known && Date.now() - known.at < VERIFY_TTL_MS) return known.promise;
    const promise = fetch(`${shop}products/${encodeURIComponent(product)}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(async (response) => {
        // Corps toujours lu ou annulé : un corps laissé ouvert garde la requête active (réseau jamais au repos).
        const json = response.ok ? await response.json().catch(() => null) : await response.body?.cancel?.().then(() => null, () => null) ?? null;
        return offerResponseVerdict(response.status, json, priceMinor);
      }, () => ({ ok: false, reason: 'error' }));
    cache.set(key, { at: Date.now(), promise });
    return promise;
  };
  // Prix du tri et du filtre « Prix » de la liste (fs-filters.js) : aucun pour une carte rétrogradée, rendu à la restauration.
  const syncFilters = (card, state) => window.FSListingFilters?.setPrice?.(card.dataset.fsRef, state === 'priced' ? Number(card.dataset.fsOfferPrice) : null);
  const apply = (card, verdict) => { const state = offerState(verdict); if (!stopped && state && applyCardState(card, state)) syncFilters(card, state); };
  const currency = typeof window.Shopify?.currency?.active === 'string' ? window.Shopify.currency.active : '';
  const country = typeof window.Shopify?.country === 'string' ? window.Shopify.country : '';
  let batching = true;
  const readBatch = (items) => {
    const products = [...new Set(items.map((item) => item.product))];
    const { query, variables } = offerBatchQuery(products, country);
    return fetch(STOREFRONT_API, { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ query, variables }) })
      .then((response) => (response.ok ? response.json() : response.body?.cancel?.().then(() => null, () => null) ?? null), () => null)
      .then((json) => (json?.data && !json.errors?.length ? Object.fromEntries(products.map((product, i) => [product, json.data[`p${i}`]])) : null), () => null);
  };
  const pump = () => {
    while (!stopped && running < 4 && queue.length) {
      const items = [];
      while (queue.length && items.length < (batching ? OFFER_BATCH : 1)) {
        const card = queue.shift();
        const product = card.dataset.fsOfferCheck, priceMinor = Number(card.dataset.fsOfferPrice);
        if (!HANDLE.test(product || '') || !Number.isSafeInteger(priceMinor)) continue;
        checkedAt.set(card, Date.now());
        items.push({ card, product, priceMinor });
      }
      // Lot complété par les cartes hors écran : toute la liste en 2 requêtes.
      if (batching) for (const card of cards) {
        if (items.length >= OFFER_BATCH) break;
        const product = card.dataset.fsOfferCheck, priceMinor = Number(card.dataset.fsOfferPrice);
        if (!items.length || queue.includes(card) || !due(card) || !HANDLE.test(product || '') || !Number.isSafeInteger(priceMinor)) continue;
        checkedAt.set(card, Date.now());
        items.push({ card, product, priceMinor, extra: true });
      }
      if (!items.length) continue;
      running += 1;
      const fresh = items.filter(({ product, priceMinor }) => { const known = cache.get(`${product}:${priceMinor}`); return !(known && Date.now() - known.at < VERIFY_TTL_MS); });
      const batch = batching && fresh.length > 1 ? readBatch(fresh) : Promise.resolve(null);
      batch.then((nodes) => {
        if (nodes === null && batching && fresh.length > 1) {
          // Refus : cartes visibles relues 4 à la fois, les autres à leur apparition.
          batching = false;
          items.forEach(({ card }) => checkedAt.delete(card));
          queue.unshift(...items.filter(({ card, extra }) => !extra || shown.has(card)).map(({ card }) => card));
          return null;
        }
        return Promise.all(items.map(({ card, product, priceMinor, extra }) => {
          if (!nodes && extra) { checkedAt.delete(card); if (shown.has(card)) enqueue(card); return null; }
          if (nodes && Object.hasOwn(nodes, product)) {
            const verdict = storefrontVerdict(nodes[product], priceMinor, currency);
            cache.set(`${product}:${priceMinor}`, { at: Date.now(), promise: Promise.resolve(verdict) });
            apply(card, verdict);
            return null;
          }
          return read(product, priceMinor).then((verdict) => apply(card, verdict));
        }));
      }).finally(() => { running -= 1; pump(); });
    }
  };
  // Revalidation : une carte revue après VERIFY_TTL_MS (défilement, retour sur l'onglet) est relue ; elle peut redevenir
  // achetable (offre republiée) ou être rétrogradée.
  const due = (card) => Date.now() - (checkedAt.get(card) ?? 0) >= VERIFY_TTL_MS;
  const enqueue = (card) => { if (due(card) && !queue.includes(card)) queue.push(card); };
  const cards = [...root.querySelectorAll('[data-fs-offer-check][data-fs-price-state="priced"]')];
  if (!cards.length) return null;
  cards.forEach((card) => { card.dataset.fsOfferPrice = card.querySelector('[data-fs-price]')?.dataset.fsPrice || ''; });
  const visible = typeof IntersectionObserver === 'function' ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) { shown.add(entry.target); enqueue(entry.target); } else shown.delete(entry.target); });
    pump();
  }, { rootMargin: '400px 0px' }) : null;
  cards.forEach((card) => (visible ? visible.observe(card) : (shown.add(card), enqueue(card))));
  const revalidate = () => {
    if (root.isConnected === false) { stop(); return; }
    if (document.visibilityState === 'visible') { shown.forEach(enqueue); pump(); }
  };
  document.addEventListener('visibilitychange', revalidate);
  const timer = window.setInterval(revalidate, VERIFY_TTL_MS);
  function stop() {
    if (stopped) return;
    stopped = true;
    queue.length = 0; shown.clear();
    visible?.disconnect();
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', revalidate);
    cardChecks.delete(root);
    delete root.dataset.fsOfferChecks;
  }
  cardChecks.set(root, stop);
  pump();
  return stop;
}

function initialise(root) {
  checkCards(root);
  if (root.dataset.fsPurchaseMounted) return;
  const account = window.FSAccount;
  /* fs-account.js (chargé avant) porte la session : sans lui, le bouton reste invisible (aucune action qui ne marche pas). */
  if (!account || account.service !== serviceBase(root.dataset.fsService)) return;
  root.dataset.fsPurchaseMounted = 'true';
  const shop = shopRoot(window);
  const terms = { href: termsHref(root.dataset.fsPurchaseTermsUrl), version: TERMS_VERSION.test(root.dataset.fsPurchaseTermsVersion || '') ? root.dataset.fsPurchaseTermsVersion : '' };
  const invite = inviteHref(root.dataset.fsPurchaseInvite);
  const checks = new Map(); // produit → { at, promise } (une lecture par minute pour toutes les fiches clonées)
  let pending = null;       // retour de Discord en attente de sa fiche

  /* ---- vérification du produit (accueil) et bouton prêt ---- */
  function verify(product, priceMinor) {
    const known = checks.get(product);
    if (known && Date.now() - known.at < VERIFY_TTL_MS) return known.promise;
    const promise = fetch(`${shop}products/${encodeURIComponent(product)}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : response.body?.cancel?.().then(() => null, () => null) ?? null)).then((json) => productVerdict(json, priceMinor), () => ({ ok: false, reason: 'error' }));
    checks.set(product, { at: Date.now(), promise });
    return promise;
  }
  function off(button) {
    /* Même hauteur que le bouton : la zone commerce ne bouge pas. */
    button.replaceWith(h('p', { class: 'fs-ld-buy-off', 'data-fs-purchase-off': true, text: TEXT.off }));
  }
  function ready(button, data) {
    button.dataset.fsPurchaseVariant = String(data.variant);
    button.setAttribute('data-fs-purchase-ready', '');
    button.addEventListener('click', () => open(button));
    if (pending && pending.listing === data.listing) { const back = pending; pending = null; open(button, back); }
  }
  /* Page restaurée du cache de navigation (Précédent, Suivant) : un bouton « Acheter » prêt est revérifié comme au
     chargement (lecture gardée VERIFY_TTL_MS) ; offre vendue, retirée ou changée entre-temps : bouton retiré. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    root.querySelectorAll('[data-fs-purchase][data-fs-purchase-ready]').forEach(async (button) => {
      const data = readPurchaseButton(button.dataset);
      if (!data?.verify) return;
      const verdict = await verify(data.product, data.priceMinor);
      if (!verdict.ok && button.isConnected && !(modal?.node.open && modal.opener === button)) off(button);
    });
  });
  async function enhance(button) {
    if (button.dataset.fsPurchaseSeen) return;
    button.dataset.fsPurchaseSeen = 'true';
    const data = readPurchaseButton(button.dataset);
    if (!data || (!data.verify && !data.variant)) { off(button); return; }
    if (!data.verify) { ready(button, data); return; }
    const verdict = await verify(data.product, data.priceMinor);
    if (!button.isConnected) return;
    if (verdict.ok) ready(button, { ...data, variant: verdict.variantId }); else off(button);
  }

  /* ---- modale (une par page, réutilisée) : dialogue du catalogue (fs-ui-dialog.js) ; Échap, × et fond refusés pendant
     la préparation du paiement, Tab bouclé dans la modale, focus rendu au bouton « Acheter ». ---- */
  const id = `fs-pu-${Math.random().toString(36).slice(2, 8)}`;
  let modal = null;
  function ensureModal() {
    if (modal) return modal;
    const status = h('p', { class: 'fs-visually-hidden', role: 'status', 'aria-live': 'polite' });
    const box = uiDialog({ size: 'sm', fullMobile: true, remove: false, className: 'fs-pu-dialog', attrs: { 'data-fs-purchase-dialog': true }, close: t('purchase.close'),
      titleAttrs: { class: 'fs-pu-title' }, bodyAttrs: { class: 'fs-pu-body' }, canClose: () => !modal.busy, onClose: () => { modal.closed = true; } });
    box.body.after(status);
    modal = { node: box.node, box, title: box.title, body: box.body, status, busy: false, closed: true, opener: null, data: null, pay: null };
    /* Retour du checkout par « Précédent » (page restaurée du cache) : la préparation est finie, « Payer » redevient actif. */
    window.addEventListener('pageshow', (event) => { if (event.persisted && modal.busy && modal.pay) setBusy(modal.pay, false); });
    return modal;
  }
  function close() {
    if (!modal?.busy) modal?.box.close();
  }
  const actions = (...buttons) => h('div', { class: 'fs-pu-actions' }, ...buttons);
  const cancel = () => h('button', { type: 'button', class: 'fs-btn fs-btn--lg fs-pu-cancel', 'data-fs-purchase-cancel': true, onclick: () => close() }, t('purchase.cancel'));
  const alertBox = (message, ...links) => h('div', { class: 'fs-alert fs-pu-alert', 'data-tone': 'danger', role: 'alert', 'data-fs-purchase-error': true },
    h('p', { text: message }), links.length ? h('p', { class: 'fs-pu-alert-links' }, ...links) : null);
  const outLink = (href, text, icon = null) => link(text, { href, before: icon, after: 'external', external: true, newTab: t('listing.new_tab').trim() });

  function show(view, focus) {
    modal.body.replaceChildren(...view);
    if (!modal.node.open) modal.closed = false;
    modal.box.open({ opener: modal.opener, focus: focus || modal.title, host: root.closest('.fs-demo') || root });
  }
  /** Étape de connexion : message, éventuelle erreur de la connexion précédente, « Se connecter avec Discord ». */
  function loginView(error = '') {
    const { data } = modal;
    const login = h('button', { type: 'button', class: 'fs-btn fs-btn--primary fs-btn--lg', 'data-fs-purchase-login': true }, discordIcon(), t('purchase.login_button'));
    login.addEventListener('click', () => {
      if (!account.login({ kind: 'purchase', game: data.game, listing: data.listing })) show([alertBox(TEXT.storage), actions(cancel())]);
    });
    const member = error === TEXT.member && invite ? outLink(invite, t('purchase.join'), discordIcon()) : null;
    show([error ? alertBox(error, ...(member ? [member] : [])) : null, h('p', { class: 'fs-pu-lead', text: TEXT.login }), actions(login, cancel())]);
  }
  /** Récapitulatif, case des CGV et « Payer ». */
  function confirmView() {
    const { data } = modal;
    const hintId = `${id}-hint`;
    const box = (name, label) => {
      const input = h('input', { type: 'checkbox', class: 'fs-checkbox', id: `${id}-${name}`, required: true, 'data-fs-purchase-consent': name, 'aria-describedby': hintId });
      return { input, row: h('div', { class: 'fs-pu-check' }, input, h('label', { for: `${id}-${name}` }, ...label)) };
    };
    const accept = box('terms', [TEXT.terms, h('a', { class: 'fs-link', href: terms.href, target: '_blank', rel: 'noopener noreferrer' }, TEXT.termsLink, external()), TEXT.termsAfter]);
    const hint = h('p', { class: 'fs-pu-hint', id: hintId, 'data-fs-purchase-hint': true });
    const alert = h('div', { class: 'fs-pu-feedback' });
    const pay = h('button', { type: 'button', class: 'fs-btn fs-btn--primary fs-btn--lg fs-btn--spinner fs-pu-pay', 'data-fs-purchase-pay': true, 'aria-describedby': hintId }, payLabel(data.priceText));
    const sync = () => {
      const state = payState({ terms: accept.input.checked });
      pay.setAttribute('aria-disabled', String(!state.ready));
      hint.textContent = state.hint;
      hint.classList.remove('fs-pu-hint--alert');
      return state.ready;
    };
    accept.input.addEventListener('change', sync);
    pay.addEventListener('click', () => {
      if (modal.busy) return;
      /* Case manquante : le bouton reste inactif, le message lié est mis en évidence (lu par le lecteur d'écran). */
      if (!sync()) { hint.classList.add('fs-pu-hint--alert'); modal.status.textContent = TEXT.hint; return; }
      buy(pay, alert);
    });
    const rows = [[t('purchase.summary_listing'), data.title || data.reference], ...(data.server ? [[t('purchase.summary_server'), data.server]] : []),
      [t('purchase.summary_price'), t('purchase.price_included', { price: data.priceText })]];
    show([
      h('dl', { class: 'fs-pu-summary' }, rows.map(([term, value]) => h('div', { class: 'fs-pu-summary-row' }, h('dt', { text: term }), h('dd', { text: value })))),
      h('p', { class: 'fs-pu-note', text: TEXT.delivery }),
      h('div', { class: 'fs-pu-consents' }, accept.row),
      hint, alert,
      actions(pay, cancel()),
    ]);
    sync();
  }
  /* Envoi en cours : roue discrète du catalogue (fs-btn--spinner, aria-busy="true") et « Préparation du paiement… »,
     gardées jusqu'à l'ouverture du checkout de Shopify. */
  function setBusy(pay, busy) {
    modal.busy = busy;
    modal.pay = busy ? pay : null;
    if (busy) pay.setAttribute('aria-busy', 'true'); else pay.removeAttribute('aria-busy');
    pay.disabled = busy;
    pay.textContent = busy ? TEXT.busy : payLabel(modal.data.priceText);
    modal.status.textContent = busy ? TEXT.busy : '';
  }
  /** Intention, panier, checkout. Une erreur s'affiche dans la modale ; la session expirée ramène à la connexion. */
  async function buy(pay, alert) {
    const { data } = modal;
    const body = intentBody({ listing: data.listing, termsVersion: terms.version });
    if (!body) { alert.replaceChildren(alertBox(TEXT.noTerms)); return; }
    setBusy(pay, true);
    /* Panier vidé pendant l'intention ; rien n'y est ajouté sans intention acceptée. */
    const cleared = cartPost('cart/clear.js').then((response) => response.ok, () => false);
    let outcome;
    try {
      const { status, body: answer } = await account.call('/purchases/intent', { method: 'POST', body });
      outcome = intentOutcome(status, answer, { priceMinor: data.priceMinor, gameName: data.gameName });
    } catch { outcome = { kind: 'error', message: TEXT.error }; }
    if (outcome.kind === 'ok') {
      const cart = await fillCart(outcome.variantId, outcome.token, cleared);
      if (cart === 'ok' && !modal.closed) {
        try { rememberCard(cardSnapshot(readCard(document, data.listing), data), window.localStorage); } catch { /* sans instantané : carte réduite */ }
        toCheckout(`${shop}checkout`); return;
      }
      outcome = cartOutcome(cart);
    }
    setBusy(pay, false);
    if (outcome.kind === 'auth') { loginView(outcome.message); return; }
    const links = [];
    if (outcome.kind === 'member' && invite) links.push(outLink(invite, t('purchase.join'), discordIcon()));
    if ((outcome.kind === 'sold' || outcome.kind === 'reserved') && data.game) links.push(h('a', { class: 'fs-link', href: `${shop}?game=${encodeURIComponent(data.game)}#fs-catalog` }, t('purchase.see_game', { game: data.gameName })));
    if (outcome.kind === 'price' || outcome.kind === 'terms') links.push(h('button', { type: 'button', class: 'fs-btn fs-btn--link', onclick: () => window.location.reload() }, t('purchase.reload')));
    /* Compte vendu ou en cours d'achat, prix ou conditions changés : plus de paiement possible depuis cette modale. Achat
       momentanément indisponible (annonce tenue, refus passager) : le bouton reste actif, l'acheteur peut réessayer. */
    if (['sold', 'reserved', 'price', 'terms', 'restricted'].includes(outcome.kind)) { pay.setAttribute('aria-disabled', 'true'); pay.disabled = true; }
    const box = alertBox(outcome.message, ...links);
    alert.replaceChildren(box);
    box.setAttribute('tabindex', '-1');
    box.focus({ preventScroll: false });
  }
  function cartPost(path, body) {
    return fetch(`${shop}${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}) });
  }
  /** Ligne ajoutée seulement après une intention acceptée (jamais sur un refus), une fois le panier vidé (`cleared`). */
  async function fillCart(variantId, token, cleared) {
    try {
      if (!(await cleared)) return 'error';
      const added = await cartPost('cart/add.js', cartAddBody(variantId, token));
      if (added.status === 422) return 'unavailable';
      return added.ok ? 'ok' : 'error';
    } catch { return 'error'; }
  }

  /** Ouvre la modale pour ce bouton : connexion d'abord sans session (ou si la connexion vient d'échouer). */
  function open(button, back = null) {
    const data = readPurchaseButton(button.dataset);
    if (!data) return;
    data.variant = variantNumericId(button.dataset.fsPurchaseVariant);
    ensureModal();
    modal.opener = button;
    modal.data = data;
    /* Espace insécable avant « · » (dans la traduction) : la référence ne commence jamais une ligne par le point médian. */
    modal.title.textContent = t('purchase.title', { game: data.gameName, reference: data.reference });
    if (!terms.version) { show([alertBox(TEXT.noTerms), actions(cancel())]); return; }
    if (back && !back.ok) { loginView(loginError(back.error)); return; }
    if (!account.session()) { loginView(); return; }
    warmCheckout();
    confirmView();
  }
  /* /checkout passe par shop.app : connexion ouverte dès le récapitulatif. */
  function warmCheckout() {
    if (document.querySelector('link[data-fs-pu-warm]')) return;
    document.head.append(h('link', { rel: 'preconnect', href: 'https://shop.app', 'data-fs-pu-warm': '' }));
  }

  /* Retour de Discord : la modale de l'annonce se rouvre dès que son bouton est prêt (fiche rouverte par ?listing= ou page). */
  if (account.returning) {
    account.returning.promise.then((detail) => {
      const back = returnIntent(detail);
      if (!back) return;
      const button = [...root.querySelectorAll('[data-fs-purchase][data-fs-purchase-ready]')].find((item) => item.dataset.fsPurchase === back.listing);
      if (button) open(button, back); else pending = back;
    });
  }
  /* Déconnexion pendant la modale : retour à l'étape de connexion. */
  document.addEventListener('fs:account', () => { if (modal?.node.open && !modal.busy && !account.session()) loginView(); });

  /* Fiches insérées dans [data-fs-detail] (clonées à chaque ouverture sur l'accueil, présentes d'emblée sur la page). */
  const scan = (node) => {
    if (node.nodeType !== 1) return;
    if (node.matches('[data-fs-purchase]')) enhance(node);
    node.querySelectorAll('[data-fs-purchase]').forEach(enhance);
  };
  const observer = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach(scan)));
  root.querySelectorAll('[data-fs-detail]').forEach((slot) => { observer.observe(slot, { childList: true, subtree: true }); scan(slot); });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-fs-purchase-enabled]').forEach(initialise);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll?.('[data-fs-purchase-enabled]').forEach(initialise));
  /* Section retirée ou remplacée (éditeur de thème) : ses vérifications de cartes s'arrêtent. */
  document.addEventListener('shopify:section:unload', (event) => stopCardChecks(event.target));
}
